import { useState, useMemo } from 'react'
import { apiError } from '../../lib/errors'
import { handlePlantLimit } from '../../lib/upsell'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { PhotoManager } from './PhotoManager'
import { EmitterEditor } from './EmitterEditor'
import { plantsApi, varietiesApi, blocksApi, valuationApi, PLANT_PURPOSES, type Plant } from '../../lib/api'
import { useAccessibility } from '../../lib/accessibility'
import { lengthUnit, inputToCm, cmToDisplay } from '../../lib/units'

// ── Design tokens (kept local so the modal is portable across pages) ──────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  red:       '#F87171',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}
const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: C.amber, fontSize: 10, fontWeight: 600,
  cursor: 'pointer', textTransform: 'none', letterSpacing: 0, padding: 0,
}
const miniBtn: React.CSSProperties = {
  flexShrink: 0, background: C.green, color: '#fff', border: 'none', borderRadius: 8,
  padding: '0 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
}

// 'dead' = tree died but stays on record (visible). Removal is the trash button
// (soft-delete). 'removed' kept for legacy rows.
const STATUSES = ['seedling', 'juvenile', 'mature', 'producing', 'declining', 'dead', 'removed']

// ── Add/Edit Plant Modal ─────────────────────────────────────────────────────
// Shared between the Plants page and the Farm Map (a plant pin opens this so the
// full plant record — variety, block, care, dimensions, photos — is editable in
// one place). `onSaved` lets a host invalidate extra queries (e.g. map features).
export function PlantModal({ plant, onClose, onSaved }: { plant: Plant | null; onClose: () => void; onSaved?: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!plant
  const system = useAccessibility().prefs.measurementSystem // imperial → in, metric → cm
  const [form, setForm] = useState({
    label:                plant?.label ?? '',
    status:               plant?.status ?? 'seedling',
    variety_id:           plant?.variety_id ?? '',
    block_id:             plant?.block_id ?? '',
    purpose:              plant?.purpose ?? '',
    root_stock:           plant?.root_stock ?? '',
    planted_at:           plant?.planted_at?.slice(0, 10) ?? '',
    // Heights are stored in cm; show in the user's unit (rounded).
    height_cm:            plant?.height_cm != null ? Math.round(cmToDisplay(plant.height_cm, system)).toString() : '',
    canopy_diameter_cm:   plant?.canopy_diameter_cm != null ? Math.round(cmToDisplay(plant.canopy_diameter_cm, system)).toString() : '',
    notes:                plant?.notes ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const { data: varietiesData } = useQuery({
    queryKey: ['varieties', 'all'],
    queryFn: () => varietiesApi.list().then(r => r.data),
  })
  const { data: blocksData } = useQuery({
    queryKey: ['blocks'],
    queryFn: () => blocksApi.list().then(r => r.data.blocks),
  })

  // Estimated value: age (from planted/grafted date) → /valuation/quote.
  const ageMonths = useMemo(() => {
    const d = form.planted_at || plant?.grafted_at?.slice(0, 10) || ''
    if (!d) return null
    const ms = Date.now() - new Date(d).getTime()
    return ms > 0 ? Math.floor(ms / (1000 * 60 * 60 * 24 * 30.44)) : 0
  }, [form.planted_at, plant])
  const selectedVariety = (varietiesData?.varieties ?? []).find(v => v.id === form.variety_id)
  const { data: quote } = useQuery({
    queryKey: ['valuation-quote', form.variety_id, selectedVariety?.category, ageMonths],
    queryFn: () => valuationApi.quote({
      kind: 'plant',
      variety_id: form.variety_id || undefined,
      category: selectedVariety?.category || undefined,
      age_months: ageMonths ?? 0,
    }).then(r => r.data),
    enabled: ageMonths != null,
  })

  // Inline "create new" for block + variety so a plant can be filed without
  // leaving the form. A block may hold MIXED varieties, so a plant's variety is
  // independent of its block.
  const [newBlockName, setNewBlockName] = useState('')
  const [showNewBlock, setShowNewBlock] = useState(false)
  const [newVarName, setNewVarName] = useState('')
  const [newVarCat, setNewVarCat] = useState('')
  const [showNewVar, setShowNewVar] = useState(false)

  const createBlock = useMutation({
    mutationFn: () => blocksApi.create({ name: newBlockName.trim(), tracking_mode: 'individual', harvest_method: 'hand' }).then(r => r.data),
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['blocks'] })
      set('block_id', b.id); setShowNewBlock(false); setNewBlockName('')
    },
  })
  const createVariety = useMutation({
    mutationFn: () => varietiesApi.create({ name: newVarName.trim(), category: newVarCat.trim() || undefined }).then(r => r.data),
    onSuccess: (v) => {
      qc.invalidateQueries({ queryKey: ['varieties', 'all'] })
      set('variety_id', v.id); setShowNewVar(false); setNewVarName(''); setNewVarCat('')
    },
  })

  const save = useMutation({
    mutationFn: (data: Partial<Plant>) =>
      (isEdit ? plantsApi.update(plant!.id, data) : plantsApi.create(data)) as Promise<unknown>,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['plants'] }); onSaved?.(); onClose() },
    onError: (e) => {
      // Plant-limit 402 → open the upgrade modal (§242) + a limit-specific message.
      if (handlePlantLimit(e)) { setError("You've reached your plant limit — see your upgrade options."); return }
      setError(apiError(e, 'Failed to save'))
    },
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.label.trim()) { setError('Plant label is required'); return }
    save.mutate({
      label: form.label, status: form.status,
      variety_id: form.variety_id || undefined,
      block_id: form.block_id || undefined,
      purpose: form.purpose || '',
      root_stock: form.root_stock,
      planted_at: form.planted_at || undefined,
      height_cm:  form.height_cm  ? Math.round(inputToCm(parseFloat(form.height_cm), system))  : undefined,
      canopy_diameter_cm: form.canopy_diameter_cm ? Math.round(inputToCm(parseFloat(form.canopy_diameter_cm), system)) : undefined,
      notes: form.notes,
    })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{isEdit ? 'Edit plant' : 'Add plant'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Label / ID *</label><input style={inp} value={form.label} onChange={e => set('label', e.target.value)} placeholder="Tree #42" required /></div>
            <div>
              <label style={lbl}>Status</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          {/* Variety — independent of block (a block may hold mixed varieties) */}
          <div>
            <label style={{ ...lbl, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Variety
              <button type="button" onClick={() => setShowNewVar(s => !s)} style={linkBtn}>{showNewVar ? 'cancel' : '＋ new variety'}</button>
            </label>
            {showNewVar ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={inp} value={newVarName} onChange={e => setNewVarName(e.target.value)} placeholder="Variety name (e.g. Sunrise)" />
                <input style={{ ...inp, maxWidth: 120 }} value={newVarCat} onChange={e => setNewVarCat(e.target.value)} placeholder="Category" />
                <button type="button" disabled={!newVarName.trim() || createVariety.isPending} onClick={() => createVariety.mutate()} style={miniBtn}>{createVariety.isPending ? '…' : 'Add'}</button>
              </div>
            ) : (
              <select style={{ ...inp, cursor: 'pointer' }} value={form.variety_id} onChange={e => set('variety_id', e.target.value)}>
                <option value="">— Select variety —</option>
                {(varietiesData?.varieties ?? []).map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
          </div>

          {/* Block — harvest-management unit (optional) */}
          <div>
            <label style={{ ...lbl, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Block
              <button type="button" onClick={() => setShowNewBlock(s => !s)} style={linkBtn}>{showNewBlock ? 'cancel' : '＋ new block'}</button>
            </label>
            {showNewBlock ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={inp} value={newBlockName} onChange={e => setNewBlockName(e.target.value)} placeholder="Block name (e.g. Block 3)" />
                <button type="button" disabled={!newBlockName.trim() || createBlock.isPending} onClick={() => createBlock.mutate()} style={miniBtn}>{createBlock.isPending ? '…' : 'Add'}</button>
              </div>
            ) : (
              <select style={{ ...inp, cursor: 'pointer' }} value={form.block_id} onChange={e => set('block_id', e.target.value)}>
                <option value="">— No block —</option>
                {(blocksData ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>
          {/* Purpose — drives valuation (fruit vs plant/graft sale) + inheritance */}
          <div>
            <label style={lbl}>Purpose</label>
            <select style={{ ...inp, cursor: 'pointer' }} value={form.purpose} onChange={e => set('purpose', e.target.value)}>
              {PLANT_PURPOSES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {quote?.matched && (quote.wholesale_usd != null || quote.retail_usd != null) && (
              <div style={{ marginTop: 6, fontSize: 12, color: C.amber }}>
                Est. value{ageMonths != null ? ` at ${ageMonths}mo` : ''}:{' '}
                {quote.wholesale_usd != null && <>${quote.wholesale_usd} wholesale</>}
                {quote.wholesale_usd != null && quote.retail_usd != null && ' · '}
                {quote.retail_usd != null && <>${quote.retail_usd} retail</>}
                {quote.schedule_name && <span style={{ color: C.textFaint }}> ({quote.schedule_name})</span>}
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Root stock</label><input style={inp} value={form.root_stock} onChange={e => set('root_stock', e.target.value)} placeholder="Turpentine" /></div>
            <div><label style={lbl}>Planted date</label><input style={inp} type="date" value={form.planted_at} onChange={e => set('planted_at', e.target.value)} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Height ({lengthUnit(system)})</label><input style={inp} type="number" min={0} value={form.height_cm} onChange={e => set('height_cm', e.target.value)} placeholder={system === 'imperial' ? '120' : '300'} /></div>
            <div><label style={lbl}>Canopy ({lengthUnit(system)})</label><input style={inp} type="number" min={0} value={form.canopy_diameter_cm} onChange={e => set('canopy_diameter_cm', e.target.value)} placeholder={system === 'imperial' ? '160' : '400'} /></div>
          </div>
          <div><label style={lbl}>Notes</label><textarea style={{ ...inp, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Observations, grafting history…" /></div>
          {plant?.id && (
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
              <PhotoManager resourceType="plant" resourceId={plant.id} label="Tree / plant photos" />
            </div>
          )}
          {plant?.id && (
            <EmitterEditor plantId={plant.id} irrigationEnabled={(plant as { irrigation_enabled?: boolean }).irrigation_enabled ?? true} />
          )}
          {error && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add plant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
